import React from 'react';
import { useState } from "react";
import Swal from "sweetalert2";
import { Button, Row, Col } from "reactstrap";
import { BiSave } from "react-icons/bi";
import { IoIosEye } from "react-icons/io";
import { TiCancel } from "react-icons/ti";
import { AiOutlineHome } from "react-icons/ai";
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { displayErrorToast, displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import { updateMoisture, getMoistureDetails, getMoistureByPlant } from "../../services/MoistureCorrectionServices";
import { getUserAllowedPlants } from '../../services/PlantServices';
import FloatingControls from '../../components/FloatingControls';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import LoadingOverlay from '../../components/LoadingOverlay';
import { applyCorrectionFactors, mapMoistureApiToInputs } from './MoistureCorrectionUtils';
import { formatDateForApi } from '../../utils/DateUtils';

function MoistureCorrectionEdit() {
  const [error] = useState({});
  const [isSubmitting] = useState(true);
  const [cookies] = useCookies(['myToken']);
  const [inProgress, setInProgress] = useState(false);
  const [inputs, setInputs] = useState({});
  const [plants, setPlants] = useState([]);
  const [originalPlantId, setOriginalPlantId] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();

  const handlePlantChange = (plantId) => {
    if (!plantId) {
      return;
    }
    const selectedPlant = plants.find((p) => String(p.value) === String(plantId));
    setInProgress(true);
    getMoistureByPlant(cookies, plantId)
      .then((response) => {
        setInProgress(false);
        if (response?.id && parseInt(response.id, 10) !== parseInt(id, 10)) {
          Swal.fire({
            icon: 'info',
            title: 'Moisture correction already exists for this plant.',
            text: 'Only edit is allowed. Open the existing record?',
            showCancelButton: true,
            confirmButtonText: 'Open existing record',
            cancelButtonText: 'Cancel',
          }).then((result) => {
            if (result.isConfirmed) {
              navigate(`/MoistureCorrectionEdit/${response.id}/`);
            } else {
              setInputs((values) => ({ ...values, plant_id: originalPlantId }));
            }
          });
          return;
        }
        setInputs((values) => ({
          ...values,
          plant_id: parseInt(plantId, 10),
          plant_name: selectedPlant?.label || values.plant_name || '',
        }));
        setOriginalPlantId(parseInt(plantId, 10));
      })
      .catch((err) => {
        setInProgress(false);
        displayError(err.response?.data ?? err.message, 'Failed to check plant moisture');
      });
  };

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    try {
      if (name === 'plant_id') {
        handlePlantChange(value);
        return;
      }
      const updates = { [name]: value };
      if (name.startsWith('moisture_')) {
        const material = name.replace('moisture_', '');
        const absorption = parseFloat(inputs[`absorption_${material}`]) || 0;
        updates[`correction_factor_${material}`] = (absorption - parseFloat(value || 0)).toFixed(2);
      } else if (name.startsWith('absorption_')) {
        const material = name.replace('absorption_', '');
        const moisture = parseFloat(inputs[`moisture_${material}`]) || 0;
        updates[`correction_factor_${material}`] = (parseFloat(value || 0) - moisture).toFixed(2);
      }
      setInputs((values) => ({ ...values, ...updates }));
    } catch (e) {
      displayErrorToast(e);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    try {
      if (!inputs.plant_id) {
        Swal.fire('Please select a plant.', '', 'info');
        return;
      }
      if ((Object.values(error).every((x) => !x)) && isSubmitting) {
        Swal.fire({
          title: 'Do you want to update?',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        }).then((result) => {
          if (result.isConfirmed) {
            setInProgress(true);
            const payload = applyCorrectionFactors({
              id: inputs.id,
              plant_id: parseInt(inputs.plant_id, 10),
              moisture_msand: inputs.moisture_msand ? parseFloat(inputs.moisture_msand) : 0,
              moisture_csand: inputs.moisture_csand ? parseFloat(inputs.moisture_csand) : 0,
              moisture_rsand: inputs.moisture_rsand ? parseFloat(inputs.moisture_rsand) : 0,
              moisture_12mm: inputs.moisture_12mm ? parseFloat(inputs.moisture_12mm) : 0,
              moisture_20mm: inputs.moisture_20mm ? parseFloat(inputs.moisture_20mm) : 0,
              absorption_msand: inputs.absorption_msand ? parseFloat(inputs.absorption_msand) : 0,
              absorption_csand: inputs.absorption_csand ? parseFloat(inputs.absorption_csand) : 0,
              absorption_rsand: inputs.absorption_rsand ? parseFloat(inputs.absorption_rsand) : 0,
              absorption_12mm: inputs.absorption_12mm ? parseFloat(inputs.absorption_12mm) : 0,
              absorption_20mm: inputs.absorption_20mm ? parseFloat(inputs.absorption_20mm) : 0,
            });
            updateMoisture(cookies, {
              ...payload,
              plant: payload.plant_id,
              sl_dt: formatDateForApi(inputs.sl_dt),
              sl_time: inputs.sl_time || '',
              correction_factor_msand: parseFloat(payload.correction_factor_msand),
              correction_factor_csand: parseFloat(payload.correction_factor_csand),
              correction_factor_rsand: parseFloat(payload.correction_factor_rsand),
              correction_factor_12mm: parseFloat(payload.correction_factor_12mm),
              correction_factor_20mm: parseFloat(payload.correction_factor_20mm),
            }, id)
              .then(() => {
                setInProgress(false);
                Swal.fire('Updated!', '', 'success');
                navigate('/MoistureCorrectionTable');
              })
              .catch((error) => {
                setInProgress(false);
                displayError(error.response?.data, "Saved Failed");
              });
          } else if (result.isDismissed) {
            Swal.fire('Not saved', '', 'info');
          }
        });
      } else {
        Swal.fire('Please check * marked fields are filled and details given are valid...', '', 'info');
      }
    } catch (e) {
      displayErrorToast(e);
    }
  };

  React.useEffect(() => {
    getUserAllowedPlants(cookies)
      .then((plantList) => {
        const plantOptions = (plantList.plant_list || [])
          .filter((obj) => obj.status)
          .map((plant) => ({ value: plant.id, label: plant.plant_alias || plant.name }));
        setPlants(plantOptions);
      })
      .catch((err) => displayError(err.response?.data ?? err.message, 'Failed to load plants'));
  }, [cookies]);

  React.useEffect(() => {
    if (id) {
      setInProgress(true);
      getMoistureDetails(cookies, id)
        .then((response) => {
          setInProgress(false);
          setInputs(mapMoistureApiToInputs(response));
          setOriginalPlantId(response.plant_id);
        })
        .catch((error) => {
          setInProgress(false);
          displayError(error.response?.data, "Loading Failed");
        });
    }
  }, [id, cookies]);

  const view = () => {
    navigate('/MoistureCorrectionTable');
  };

  const Back = () => {
    navigate('/Home');
  };

  const Cancel = () => {
    if (id) {
      setInProgress(true);
      getMoistureDetails(cookies, id)
        .then((response) => {
          setInProgress(false);
          setInputs(mapMoistureApiToInputs(response));
          setOriginalPlantId(response.plant_id);
        })
        .catch((error) => {
          setInProgress(false);
          displayError(error.response?.data, 'Loading Failed');
        });
    }
  };

  return (
    <>
      <div id="outer-container" className="App">
        <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
        <ErrorToast />
        <LoadingOverlay inProgress={inProgress} />
        <div id="page-wrap">
          <form onSubmit={handleSubmit} data-id={inputs.id}>
            <div className="container">
              <PremiumPageHeader
                module="QUALITY CONTROL"
                title="Update Moisture Correction"
                subtitle="Edit moisture and absorption for this plant. CF% = Absorption − Moisture. Changes apply to new DC batch targets and the solar batching report."
                modeLabel="EDIT MODE"
              />
            </div>
            <FloatingControls
              tableLink="/MoistureCorrectionTable"
              formLink="/MoistureCorrection"
              addNewLabel="New Moisture Correction"
              onCancel={Cancel}
              enableCancel={true}
            />
            <div className="container">
              <Row>
                <Col xl={12} lg={12} md={12}>
                  <div className="form-row table-bordered shadow p-2 my-2 border-secondary bg-blue form-control-panel">
                    <label htmlFor="plant_id" className="form-group col-sm-2 text-left">Plant</label>
                    <select
                      id="plant_id"
                      name="plant_id"
                      className="form-control col-sm-3 browser-default custom-select mandatory-form-control"
                      required
                      value={inputs.plant_id || ''}
                      onChange={handleChange}
                    >
                      <option value="">Select plant</option>
                      {plants.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </select>

                    <label htmlFor="mc_no" className="form-group col-sm-2 text-right">MC No</label>
                    <input
                      id="mc_no"
                      name="mc_no"
                      type="text"
                      readOnly
                      className="form-control col-sm-2"
                      value={inputs.mc_no || ''}
                    />

                    <label htmlFor="sl_dt" className="form-group col-sm-1 text-right">Date</label>
                    <input
                      id="sl_dt"
                      name="sl_dt"
                      type="text"
                      className="form-control col-sm-2 mandatory-form-control"
                      placeholder="DD-MM-YYYY"
                      required
                      value={inputs.sl_dt || ''}
                      onChange={handleChange}
                    />

                    <div className="w-100" />
                    <label htmlFor="sl_time" className="form-group col-sm-2 text-right">Time</label>
                    <input
                      id="sl_time"
                      name="sl_time"
                      type="time"
                      className="form-control col-sm-2 mandatory-form-control"
                      required
                      value={inputs.sl_time || ''}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
              </Row>
              <div className="container form-control-panel">
                <div className="form-row align-items-center mb-2">
                  <h5 className="col-sm-6 mb-0"><b>Material Correction</b></h5>
                </div>
                <Row>
                  <Col xl={12} lg={12} md={12}>
                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary bg-blue form-control-panel moisture-correction-form">
                      <div className="table-responsive w-100">
                        <table className="table table-bordered moisture-correction-table mb-0">
                          <thead>
                            <tr>
                              <th className="moisture-correction-col-material">Material</th>
                              <th>Moisture (%)</th>
                              <th>Absorption (%)</th>
                              <th>Correction Factor (%)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[
                              ['msand', 'MSand'],
                              ['csand', 'CSand'],
                              ['rsand', 'RSand'],
                              ['12mm', '12 mm'],
                              ['20mm', '20 mm'],
                            ].map(([key, label]) => (
                              <tr key={key}>
                                <td className="moisture-correction-material-cell">{label}</td>
                                <td>
                                  <input
                                    type="number"
                                    step="any"
                                    name={`moisture_${key}`}
                                    onChange={handleChange}
                                    value={inputs[`moisture_${key}`] || 0}
                                    max="15"
                                    min="0"
                                    className="form-control mandatory-form-control"
                                    required
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    step="any"
                                    name={`absorption_${key}`}
                                    onChange={handleChange}
                                    value={inputs[`absorption_${key}`] || 0}
                                    max="5"
                                    min="0"
                                    className="form-control mandatory-form-control"
                                    required
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    name={`correction_factor_${key}`}
                                    value={inputs[`correction_factor_${key}`] || 0}
                                    className="form-control moisture-correction-cf-input"
                                    disabled
                                    required
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            <div className="footer company-form-footer text-center">
              <div className="company-form-footer-actions">
                <Button type="submit" className="company-form-action-btn">
                  <BiSave size={18} />
                  Update
                </Button>
                <Button type="button" className="company-form-action-btn" onClick={Cancel}>
                  <TiCancel size={18} />
                  Reset
                </Button>
                <Button type="button" className="company-form-action-btn" onClick={view}>
                  <IoIosEye size={18} />
                  View
                </Button>
                <Button type="button" className="company-form-action-btn" onClick={Back}>
                  <AiOutlineHome size={18} />
                  Home
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default MoistureCorrectionEdit;
